/**
 * Direct label printing via QZ Tray (OS printer drivers) with browser print fallback.
 * Install QZ Tray from https://qz.io/download/ for silent driver printing.
 */

const QZ_CDN = 'https://cdn.jsdelivr.net/npm/qz-tray@2.2.4/qz-tray.js';
const DEFAULT_PRINTER_KEY = 'omnichek.defaultLabelPrinterId';

let qzLoadPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

export async function ensureQzTray() {
  if (typeof window !== 'undefined' && window.qz) return window.qz;
  if (!qzLoadPromise) {
    qzLoadPromise = loadScript(QZ_CDN).then(() => window.qz);
  }
  return qzLoadPromise;
}

export async function isQzTrayAvailable() {
  try {
    const qz = await ensureQzTray();
    if (!qz?.websocket) return false;
    if (!qz.websocket.isActive()) {
      await qz.websocket.connect({ retries: 1, delay: 0.5 });
    }
    return qz.websocket.isActive();
  } catch {
    return false;
  }
}

export async function discoverSystemPrinters() {
  const qz = await ensureQzTray();
  if (!qz.websocket.isActive()) {
    await qz.websocket.connect();
  }
  const printers = await qz.printers.find();
  return Array.isArray(printers) ? printers : [printers].filter(Boolean);
}

export function getStoredDefaultPrinterId() {
  try {
    return localStorage.getItem(DEFAULT_PRINTER_KEY) || '';
  } catch {
    return '';
  }
}

export function setStoredDefaultPrinterId(id) {
  try {
    if (id) localStorage.setItem(DEFAULT_PRINTER_KEY, id);
    else localStorage.removeItem(DEFAULT_PRINTER_KEY);
  } catch {
    /* ignore */
  }
}

function labelCaption(label) {
  return (
    label.unit_label ||
    label.unit_display ||
    label.unit_code ||
    [label.product_name, label.sku, label.qr_id?.slice(0, 8)].filter(Boolean).join(' · ')
  );
}

async function printImageLabel(qz, printerName, label, profile) {
  const w = profile?.width_mm || 50;
  const h = profile?.height_mm || 30;
  const data = (label.qr_image || '').replace(/^data:image\/\w+;base64,/, '');
  if (!data) return;
  const config = qz.configs.create(printerName, {
    size: { width: w, height: h },
    units: 'mm',
    density: profile?.dpi || 203,
  });
  const caption = labelCaption(label);
  await qz.print(config, [
    {
      type: 'pixel',
      format: 'image',
      flavor: 'base64',
      data,
      options: { language: 'ESCPOS' },
    },
    {
      type: 'raw',
      format: 'plain',
      data: `\n${caption}\n`,
    },
  ]);
}

async function printZplLabel(qz, printerName, label, profile) {
  const caption = labelCaption(label).replace(/\^/g, ' ');
  const payload = label.public_url || label.qr_id || '';
  const zpl =
    '^XA\n' +
    '^FO30,20^BQN,2,5\n' +
    `^FDQA,${payload}^FS\n` +
    `^FO30,170^A0N,20,20^FD${caption}^FS\n` +
    '^XZ\n';
  const config = qz.configs.create(printerName);
  await qz.print(config, [{ type: 'raw', format: 'plain', data: zpl }]);
}

/**
 * Print labels to a mapped printer profile. Falls back to browser print when QZ unavailable.
 */
export async function printLabelsDirect({ labels, printerProfile, fallbackPrint }) {
  if (!labels?.length) return { mode: 'none', count: 0 };

  const profile = printerProfile;
  const useQz = profile && profile.driver_type !== 'browser';

  if (useQz) {
    try {
      const ok = await isQzTrayAvailable();
      if (ok && profile.system_printer_name) {
        const qz = await ensureQzTray();
        for (const label of labels) {
          if (profile.print_language === 'zpl' || profile.driver_type === 'qz_zpl') {
            await printZplLabel(qz, profile.system_printer_name, label, profile);
          } else {
            await printImageLabel(qz, profile.system_printer_name, label, profile);
          }
        }
        return { mode: 'qz', count: labels.length };
      }
    } catch (err) {
      console.warn('QZ print failed, falling back to browser', err);
    }
  }

  if (typeof fallbackPrint === 'function') {
    await Promise.resolve(fallbackPrint());
    return { mode: 'browser', count: labels.length };
  }
  return { mode: 'skipped', count: 0 };
}
